/**
 * "Linked chats" section on Screen 3.
 *
 * NOTE: the API's `/status` endpoint only returns a `linked_chats_count`
 * scalar — not the actual list of chats. That was an intentional API
 * design choice (per-user chat details are Telegram user IDs, which we
 * don't want to leak). Until a list-chats endpoint exists we show the
 * count and offer "add another chat" + a bulk "disable all" affordance.
 *
 * TODO: when `GET /v1/projects/:id/auto-campaign/chats` lands, swap this
 * for a real row-per-chat list with per-chat disable buttons calling
 * `useDisableTelegramChat`.
 */
import { MessageCircle, Plus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Section, SectionHeader, SectionTitle } from '@/components/ui/section';
import type { AutoCampaignStatus } from '@/features/auto-campaign/types';

export interface LinkedChatsListProps {
  status: AutoCampaignStatus;
  onAddChat: () => void;
}

export function LinkedChatsList({
  status,
  onAddChat,
}: LinkedChatsListProps): JSX.Element {
  return (
    <Section>
      <SectionHeader>
        <SectionTitle className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          Linked chats
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {status.linked_chats_count}
          </span>
        </SectionTitle>
      </SectionHeader>
      <div className="space-y-2">
        {status.linked_chats_count === 0 ? (
          <p className="text-sm text-muted-foreground">
            No chats linked yet. Add one to receive digests.
          </p>
        ) : (
          <div className="flex items-center gap-3 rounded-md border border-border/60 bg-card/40 p-3">
            <MessageCircle className="h-4 w-4 text-muted-foreground" />
            <div className="flex-1 text-sm">
              <span className="font-medium">
                {status.linked_chats_count} chat
                {status.linked_chats_count === 1 ? '' : 's'} linked
              </span>
              <span className="ml-2 text-xs text-muted-foreground">
                Digests will be delivered to all linked chats.
              </span>
            </div>
          </div>
        )}
        <Button variant="outline" size="sm" onClick={onAddChat}>
          <Plus className="h-3.5 w-3.5" />
          Add another chat
        </Button>
      </div>
    </Section>
  );
}
